// 전투 판정: 원·부채꼴·선분 판정, 데미지·치명, 히트스톱, 넉백, 콤보·칭찬, 대량 처치.
import { AWAKEN, CAMERA_PUNCH, MP_ECON, COMBO_COUNTER, DAMAGE_CAP, HERO, HIT_PUNCH, HIT_STATE, EXECUTE, HITSTOP, JUGGLE, JUST_DODGE, LIGHT_PUNCH, LINK, MOBS, POSTURE, POSTURE_NORMAL, PRAISE_TIERS, SHIELD_GUARD } from "../shared/config";
import { Ev, HitFlag, type GameEvent } from "../shared/events";
import type { LaunchGrant } from "../shared/skills/types";
import { XpCause, killXp } from "../shared/growth";
import { HeroAct, MobKind, MobState, SKILL_MAX, Scene, combatScene, TeleShape, type TelegraphSlot } from "../shared/types";
import type { GameSim } from "./game";
import { LEGEND_UNIQUES, UNIQUE_SHIELD_TICKS } from "../shared/content/items";

/** 고유 효과 칸 → Ev.GearUnique a(LEGEND_UNIQUES 번호) */
const UNIQUE_INDEX = (key: string): number => LEGEND_UNIQUES.findIndex((u) => key in u.fx);
const UX = { lowHpDmg: UNIQUE_INDEX("lowHpDmg"), eliteDmg: UNIQUE_INDEX("eliteDmg"), critRefund: UNIQUE_INDEX("critRefund"), dodgeShield: UNIQUE_INDEX("dodgeShield"), killHeal: UNIQUE_INDEX("killHeal"), killMp: UNIQUE_INDEX("killMp"), killAwaken: UNIQUE_INDEX("killAwaken") } as const;

/** '벼랑 끝 칼날'(HP 30% 이하 피해) 발동 알림 최소 간격(틱): 피해 굴림마다가 아니라 1초에 한 번만 Ev.GearUnique를 낸다(c017 가장자리 맥동) */
export const LOW_HP_CUE_TICKS = 60;

/** 타격 원인. Ev.Kill.d와 같은 값 */
export const HitSource = { Basic: 0, Skill: 1, Awaken: 2 } as const;
export type HitSourceId = (typeof HitSource)[keyof typeof HitSource];
/** 한 판정 묶음이 이미 맞힌 대상. 검기는 자기 표식을, 효과 판정은 공용 표식을 쓴다 */
export interface HitMark {
  readonly mobs: Uint8Array; boss: boolean;
  /** 이 판정 묶음(효과 한 번·검기 한 발)이 이미 준 처형 보상 수. 여러 틱에 걸쳐 나아가는 검기도 한 발 전체에서 EXECUTE.maxRewardsPerAction회까지 */
  rewards?: number;
  /** 이 판정 묶음의 처치 수·대량 처치 보고 여부. 검기 한 발이 여러 틱에 걸쳐 20마리를 쓰러뜨려도 MassKill이 한 번 난다 */
  kills?: number;
  mass?: boolean;
  /** 이 판정 묶음(라이라 화살 한 발)이 평타 MP를 이미 채웠는지(여러 틱을 날아도 한 발에 1회) */
  mp?: boolean;
  /** 발사 순간의 연계 단계(검기). 날아가는 동안 다른 스킬로 캔슬해 단계가 올라도 한 발의 피해는 같다. 없으면 현재 단계 */
  linkStage?: number;
}
/** 기존 전투 소비자의 import 경로는 유지하고 비트 정의는 공유 이벤트에서 가져온다. */
export { HitFlag } from "../shared/events";
/** 처치 반응 세기: 0 일반 타격, 1 마무리타·스킬, 2 각성(DEATH_LAUNCH) */
export const KillPower = { Basic: 0, Heavy: 1, Awaken: 2 } as const;
const GUARD_COS = Math.cos((SHIELD_GUARD.halfArcDeg * Math.PI) / 180);

export function circleHit(x: number, z: number, tx: number, tz: number, range: number, targetRadius = 0): boolean {
  const dx = tx - x, dz = tz - z, r = range + targetRadius;
  return dx * dx + dz * dz <= r * r;
}
export function coneHit(x: number, z: number, facing: number, range: number, arc: number, tx: number, tz: number, targetRadius = 0): boolean {
  const dx = tx - x, dz = tz - z, distance = Math.hypot(dx, dz);
  if (distance > range + targetRadius) return false;
  if (distance <= targetRadius || arc >= Math.PI * 2) return true;
  const dot = (dx * Math.sin(facing) + dz * Math.cos(facing)) / distance;
  return dot >= Math.cos(Math.min(Math.PI, arc / 2 + Math.asin(Math.min(1, targetRadius / distance))));
}
/** 폭은 선분 전체 두께다. */
export function segmentHit(x0: number, z0: number, x1: number, z1: number, width: number, tx: number, tz: number, targetRadius = 0): boolean {
  const dx = x1 - x0, dz = z1 - z0, lengthSq = dx * dx + dz * dz;
  const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((tx - x0) * dx + (tz - z0) * dz) / lengthSq));
  return circleHit(x0 + dx * t, z0 + dz * t, tx, tz, width / 2, targetRadius);
}
/** facing 규약(atan2(dx, dz))의 방향. 두 점이 겹치면 fallback */
export function headingTo(x0: number, z0: number, x1: number, z1: number, fallback: number): number {
  const dx = x1 - x0, dz = z1 - z0;
  return dx * dx + dz * dz < 1e-12 ? fallback : Math.atan2(dx, dz);
}
/** 렌더가 그리는 선 데칼: 시작점에서 angle 방향 길이 len, 전체 폭 width의 직사각형 안인지 */
export function lineRectHit(x0: number, z0: number, angle: number, len: number, width: number, tx: number, tz: number): boolean {
  const dx = Math.sin(angle), dz = Math.cos(angle), rx = tx - x0, rz = tz - z0;
  const along = rx * dx + rz * dz, side = rx * dz - rz * dx;
  return along >= 0 && along <= len && Math.abs(side) <= width / 2;
}
/**
 * 경고 데칼과 같은 모양으로 판정한다(대상 반지름 0 = 영웅 중심점 기준).
 * 데칼 밖에 서 있으면 안전하고, 안에 있으면 맞는다.
 */
export function telegraphHit(t: TelegraphSlot, tx: number, tz: number): boolean {
  if (t.shape === TeleShape.Circle) return circleHit(t.x, t.z, tx, tz, t.radius, 0);
  if (t.shape === TeleShape.Cone) return coneHit(t.x, t.z, t.angle, t.radius, t.width, tx, tz, 0);
  return lineRectHit(t.x, t.z, t.angle, t.radius, t.width, tx, tz);
}

export class Combat {
  private readonly touched: Uint32Array;
  private pulse = 1;
  private bossPulse = 0;
  private actionKills = 0;
  private massReported = false;
  private source: HitSourceId = HitSource.Basic;
  private multiHit = false;
  /** 이번 판정 묶음이 맞은 몹에게 거는 런처 띄움(스킬 타임라인 grants.launch). null이면 기존 규칙(경직·마무리 띄움) */
  private launch: LaunchGrant | null = null;
  /** 이번 동작에서 카메라 펀치를 이미 냈는지 */
  private punched = false;
  /** 이번 기본 공격 동작(라이라는 화살 한 발)이 이미 MP를 채웠는지(MP_ECON, 동작당 1회) */
  private mpGiven = false;
  /** 저스트 회피 강화가 붙은 이번 동작의 첫 실제 타격을 찾는다 */
  private counterEligible = false;
  private counterUsed = false;
  /** 이번 동작에서 처형 보상을 준 횟수(EXECUTE.maxRewardsPerAction) */
  private executeRewards = 0;
  /** 판정 순간 효과(SkillBurst·검기)의 연계 배율 제외 여부. 수치는 표 그대로 쓴다 */
  private plainHit = false;
  /** 효과 판정 중 연계 단계 고정값(검기 한 발의 발사 순간 단계). -1이면 현재 단계 */
  private linkStageFixed = -1;
  /** 효과 판정 전후로 동작 상태를 되돌리기 위한 보관(할당 없음) */
  private readonly saved = { source: HitSource.Basic as HitSourceId, mpGiven: false, multiHit: false, counterEligible: false, counterUsed: false, plainHit: false, punched: false, actionKills: 0, massReported: false, executeRewards: 0, linkStageFixed: -1 };
  /** 한 번의 효과 판정 안에서 같은 적을 한 번만 맞히는 표식(동작의 touched와 따로 쓴다) */
  private readonly burstMark: HitMark;
  constructor(private readonly g: GameSim) {
    this.touched = new Uint32Array(g.mobs.capacity);
    this.burstMark = { mobs: new Uint8Array(g.mobs.capacity), boss: false, rewards: 0, kills: 0, mass: false };
  }

  /** 한 동작(공격 1타·스킬 1회·각성 1회) 시작. 대량 처치는 동작 단위로 센다. */
  beginAction(source: HitSourceId = HitSource.Basic): void {
    this.source = source; this.multiHit = false; this.punched = false; this.mpGiven = false;
    this.counterEligible = (source === HitSource.Basic || source === HitSource.Skill) && this.g.hasJustDodgeCounter();
    this.counterUsed = false; this.executeRewards = 0;
    this.newPulse(); this.actionKills = 0; this.massReported = false;
  }
  /** 같은 동작 안의 다음 다단 타격. 각 대상을 다시 맞힐 수 있다 */
  newPulse(): void {
    this.pulse = (this.pulse + 1) >>> 0;
    if (this.pulse === 0) { this.touched.fill(0); this.bossPulse = 0; this.pulse = 1; }
  }
  /** 다단 타격의 마지막이 아닌 타격이면 true(Hit flags 16) */
  setMultiHit(intermediate: boolean): void { this.multiHit = intermediate; }
  /** 다음 판정(cone·circle·line)이 맞힌 몹을 띄울 런처 부여. 판정 직후 null로 되돌린다(스킬 타임라인 실행기가 부른다) */
  setLaunch(grant: LaunchGrant | null): void { this.launch = grant; }
  /** 장비 흡혈의 소수 몫(정수 1이 모이면 회복한다) */
  private lifeBank = 0;
  /** 이번 판 남은 버팀(세트 효과: 쓰러질 피해를 HP 1로 버팀) */
  lastStandLeft = 0;
  /** 전설 고유 효과 보호막(남은 흡수량·남은 틱)과 치명타 재사용 감소를 마지막으로 적용한 틱(한 틱 한 번) */
  shield = 0;
  shieldTicks = 0;
  private refundTick = -1;
  /** '벼랑 끝 칼날' 발동 알림을 마지막으로 낸 틱(LOW_HP_CUE_TICKS 간격) */
  private lowHpCueTick = -1e9;
  /** 스킬별 마지막 시전 뒤 '칼날의 박자'로 줄인 재사용 대기 합(틱). 시전 때 0. 장비 감소 합이 BUFF_CAP.cooldown을 넘지 않게 센다 */
  readonly refunded = new Float32Array(SKILL_MAX);
  reset(): void {
    this.lifeBank = 0; this.lastStandLeft = this.g.stats.lastStand; this.shield = 0; this.shieldTicks = 0; this.refundTick = -1; this.lowHpCueTick = -1e9; this.refunded.fill(0);
    this.touched.fill(0); this.pulse = 1; this.bossPulse = 0; this.actionKills = 0; this.massReported = false;
    this.source = HitSource.Basic; this.multiHit = false; this.launch = null; this.punched = false; this.mpGiven = false; this.counterEligible = false; this.counterUsed = false; this.executeRewards = 0;
  }
  /** 영웅 시간 영역: 콤보 무타격 타이머 */
  step(): void {
    const c = this.g.combo;
    if (c.timer > 0 && --c.timer === 0) { c.count = 0; c.praiseTier = 0; }
    if (this.shieldTicks > 0 && --this.shieldTicks === 0) this.shield = 0;
  }
  private unique(index: number, x: number, z: number, amount: number): void {
    const e = this.g.emit(Ev.GearUnique); e.x = x; e.z = z; e.a = index; e.b = amount;
  }
  /** 회피 시작(hero.startDodge): '회피의 방패' 보호막(최대 HP 비율, 3초, 덮어쓰지 않고 큰 쪽) */
  onDodge(): void {
    const g = this.g, r = g.stats.dodgeShield;
    if (!(r > 0) || !combatScene(g.flow.scene)) return;
    const amount = Math.max(1, Math.round(g.hero.maxHp * r));
    this.shield = Math.max(this.shield, amount); this.shieldTicks = UNIQUE_SHIELD_TICKS;
    this.unique(UX.dodgeShield, g.hero.x, g.hero.z, amount);
  }
  /** 스킬 시전(hero.startSkill): 그 스킬의 환급 합을 0으로(새 재사용 대기의 장비 감소 예산을 다시 연다) */
  onSkillCast(index: number): void { if (index >= 0 && index < this.refunded.length) this.refunded[index] = 0; }
  /**
   * 치명타: '칼날의 박자' 모든 스킬 재사용 대기 감소(한 틱에 한 번).
   * BUFF_CAP.cooldown(40%): 장비 옵션 감소(skillCooldown이 이미 반영)와 이 환급을 합쳐 장비 전 재사용의 40%까지만 줄인다 —
   * 시전 뒤 환급 합 ≤ skillCooldown − skillCooldownFloor(c015, 두 개를 끼면 치명마다 24틱이던 무제한 환급 차단, PR #154 리뷰 이월)
   */
  private critRefund(): void {
    const g = this.g, t = g.stats.critRefund, cd = g.hero.cooldowns, st = g.stats;
    if (!(t > 0) || this.refundTick === g.tick) return;
    this.refundTick = g.tick;
    let most = 0;
    for (let i = 0; i < cd.length && i < this.refunded.length; i++) {
      if (!(cd[i] > 0)) continue;
      const room = Math.max(0, st.skillCooldown[i] - st.skillCooldownFloor[i] - this.refunded[i]);
      const n = Math.min(t, cd[i], room);
      if (n <= 0) continue;
      cd[i] -= n; this.refunded[i] += n;
      if (n > most) most = n;
    }
    if (most > 0) this.unique(UX.critRefund, g.hero.x, g.hero.z, most);
  }
  stop(ticks: number): void {
    if (ticks <= this.g.hitstop) return;
    this.g.hitstop = ticks;
    this.g.emit(Ev.Hitstop).a = ticks;
  }
  cone(range: number, arc: number, mult: number, finisher = false): void {
    const h = this.g.hero;
    this.resolve(TeleShape.Cone, h.x, h.z, range, h.facing, arc, mult, finisher, true, null);
  }
  circle(range: number, mult: number, finisher = false, hitstop = true): void {
    const h = this.g.hero;
    this.resolve(TeleShape.Circle, h.x, h.z, range, h.facing, 0, mult, finisher, hitstop, null);
  }
  line(x: number, z: number, endX: number, endZ: number, width: number, mult: number, finisher = false): void {
    this.resolve(TeleShape.Line, x, z, endX, endZ, width, mult, finisher, true, null);
  }
  /**
   * 효과 판정(SkillBurst 뒤에 부른다): 피해 원인은 스킬, 연계 배율은 빼고 수치 그대로, 한 효과 안에서 대상당 한 번.
   * 동작(공격·스킬) 상태는 호출 뒤 그대로 돌아간다.
   */
  burstCircle(x: number, z: number, radius: number, mult: number): void {
    this.clearBurstMark();
    this.enterEffect(true, this.burstMark);
    this.resolve(TeleShape.Circle, x, z, radius, 0, 0, mult, false, true, this.burstMark);
    this.leaveEffect(this.burstMark);
  }
  burstLine(x: number, z: number, endX: number, endZ: number, width: number, mult: number): void {
    this.clearBurstMark();
    this.enterEffect(true, this.burstMark);
    this.resolve(TeleShape.Line, x, z, endX, endZ, width, mult, false, true, this.burstMark);
    this.leaveEffect(this.burstMark);
  }
  /** 초승달 검기 한 틱 구간. 연계 배율은 검기 스킬 피해이므로 적용한다. mark는 그 검기 한 발의 표식 */
  waveLine(x: number, z: number, endX: number, endZ: number, width: number, mult: number, mark: HitMark, plain = false): void {
    this.enterEffect(plain, mark);
    this.resolve(TeleShape.Line, x, z, endX, endZ, width, mult, false, true, mark);
    this.leaveEffect(mark);
  }
  /** 화살·장판 판정 중 Hit 이벤트에 더하는 비트(HitFlag.Arrow·Pierce, c022). 판정이 끝나면 0 */
  private extraFlags = 0;
  /** 화살 첫 적중 판정이 맞힌 대상의 위치(폭발·회오리 착탄점) */
  lastHitX = 0;
  lastHitZ = 0;
  /**
   * 화살 한 틱 구간(c022 라이라, game/arrows.ts). mark는 그 화살 한 발의 표식(같은 적은 한 번만), source는 기본 공격 화살이면 Basic(연계 시간 갱신·기본 공격 피해 옵션),
   * 스킬이면 Skill. pierce면 구간 안의 적을 모두, 아니면 시작점에서 가장 가까운 한 대상만 맞히고 true를 돌려준다(화살이 멈춘다, 위치는 lastHitX·Z).
   * launch는 런처(올려쏘기) 띄움, finisher는 마무리 계열(넉백·띄우기 처치 반응)
   */
  arrowSegment(x: number, z: number, endX: number, endZ: number, width: number, mult: number, mark: HitMark, source: HitSourceId, pierce: boolean, finisher: boolean, launch: LaunchGrant | null): boolean {
    this.enterEffect(false, mark, source);
    this.launch = launch;
    this.extraFlags = HitFlag.Arrow | (pierce ? HitFlag.Pierce : 0);
    let stopped = false;
    if (pierce) this.resolve(TeleShape.Line, x, z, endX, endZ, width, mult, finisher, true, mark);
    else stopped = this.firstOnSegment(x, z, endX, endZ, width, mult, finisher, mark);
    this.extraFlags = 0;
    this.launch = null;
    this.leaveEffect(mark);
    return stopped;
  }
  /**
   * 장판·폭발 판정(c022): 원 안의 적을 한 번씩. mark는 이 판정(다단이면 한 번)의 표식. multiHit = 다단 중간타(Hit flags 16).
   * 연계 배율은 mark.linkStage(놓은 순간 단계)로 적용한다(스킬 피해)
   */
  zoneCircle(x: number, z: number, radius: number, mult: number, mark: HitMark, multiHit: boolean, finisher: boolean): void {
    this.enterEffect(false, mark);
    this.multiHit = multiHit;
    this.resolve(TeleShape.Circle, x, z, radius, 0, 0, mult, finisher, true, mark);
    this.leaveEffect(mark);
  }
  /** 선분 위에서 시작점에 가장 가까운 대상 한 명만 맞힌다. 맞혔으면 true */
  private firstOnSegment(x: number, z: number, endX: number, endZ: number, width: number, mult: number, finisher: boolean, mark: HitMark): boolean {
    const g = this.g, m = g.mobs, dx = endX - x, dz = endZ - z, len2 = dx * dx + dz * dz;
    let best = Infinity, id = -1, boss = false;
    for (let i = 0; i < m.capacity; i++) {
      const st = m.state[i];
      if (st === MobState.Inactive || st === MobState.Dying || st === MobState.Wakeup || mark.mobs[i] === 1) continue;
      if (!segmentHit(x, z, endX, endZ, width, m.x[i], m.z[i], g.mobController.radius(i))) continue;
      const t = len2 === 0 ? 0 : ((m.x[i] - x) * dx + (m.z[i] - z) * dz) / len2;
      if (t < best) { best = t; id = i; }
    }
    const b = g.boss;
    if (g.bossController.vulnerable && !mark.boss && segmentHit(x, z, endX, endZ, width, b.x, b.z, b.radius)) {
      const t = len2 === 0 ? 0 : ((b.x - x) * dx + (b.z - z) * dz) / len2;
      if (t < best) { best = t; id = -1; boss = true; }
    }
    if (boss) { mark.boss = true; this.lastHitX = b.x; this.lastHitZ = b.z; this.damageBoss(mult, finisher, true); return true; }
    if (id < 0) return false;
    mark.mobs[id] = 1; this.lastHitX = m.x[id]; this.lastHitZ = m.z[id];
    this.damageMob(id, mult, finisher, true);
    return true;
  }
  private clearBurstMark(): void { const k = this.burstMark; k.mobs.fill(0); k.boss = false; k.rewards = 0; k.kills = 0; k.mass = false; k.mp = false; }
  /**
   * 효과 판정 문맥 저장: 바깥 동작의 source·다단·저스트 회피 상태, 카메라 펀치 1회 상태(punched), 대량 처치 카운터(actionKills·massReported)를 보관한다.
   * 효과의 처치는 효과 자신의 카운터로 세므로, 바깥 동작의 20처치 판정과 섞이지 않는다.
   * 효과는 자기 펀치를 한 번 낼 수 있고(punched를 false로 시작), 끝나면 바깥 동작의 상태를 그대로 돌려준다.
   */
  private enterEffect(plain: boolean, mark: HitMark, source: HitSourceId = HitSource.Skill): void {
    const s = this.saved;
    s.source = this.source; s.multiHit = this.multiHit; s.counterEligible = this.counterEligible; s.counterUsed = this.counterUsed; s.plainHit = this.plainHit;
    s.punched = this.punched; s.linkStageFixed = this.linkStageFixed; s.mpGiven = this.mpGiven; this.mpGiven = mark.mp ?? false;
    // 저스트 회피 반격: 진홍 초승달 본 검기(plain=false)는 그 스킬의 첫 실제 타격이므로 반격 자격을 이어받는다. 추가 효과(plain)만 뺀다
    // c022: 라이라 기본 공격 화살은 source = Basic(연계 시간 갱신·기본 공격 피해 옵션·방패 막기 규칙이 근접 기본 공격과 같다)
    this.source = source; this.multiHit = false; this.launch = null; this.counterEligible = plain ? false : s.counterEligible; this.plainHit = plain; this.punched = false;
    this.linkStageFixed = mark.linkStage ?? -1;
    // 대량 처치는 효과 자신의 카운터로 센다: 바깥 동작의 처치 수와 섞지 않는다(20처치 MassKill은 동작마다 한 번).
    // 카운터는 표식(mark)에 붙어 있어 여러 틱에 걸친 검기 한 발도 한 묶음으로 센다(Codex 6차)
    s.actionKills = this.actionKills; s.massReported = this.massReported;
    this.actionKills = mark.kills ?? 0; this.massReported = mark.mass ?? false;
    // 처형 보상도 효과마다 자기 예산(EXECUTE.maxRewardsPerAction)을 쓴다: 지연 효과(낙화 여진 등)가 발동 시점에 우연히 진행 중인
    // 다른 동작의 예산을 빌리면 보상이 그 사이 입력에 따라 달라진다(Codex 4차). 예산은 표식(mark)에 붙어 있어 여러 틱에 걸친
    // 검기 한 발도 전체에서 한 예산을 쓴다(Codex 5차). 끝나면 바깥 동작의 예산을 그대로 돌려준다
    s.executeRewards = this.executeRewards; this.executeRewards = mark.rewards ?? 0;
  }
  private leaveEffect(mark: HitMark): void {
    const s = this.saved;
    mark.rewards = this.executeRewards; mark.kills = this.actionKills; mark.mass = this.massReported; mark.mp = this.mpGiven;
    // 본 검기가 반격을 썼으면 바깥 동작에도 쓴 것으로 남긴다(다음 동작으로 넘어가지 않게)
    const keepCounter = !this.plainHit;
    const ce = this.counterEligible, cu = this.counterUsed;
    this.source = s.source; this.multiHit = s.multiHit; this.counterEligible = s.counterEligible; this.counterUsed = s.counterUsed; this.plainHit = s.plainHit;
    if (keepCounter) { this.counterEligible = ce; this.counterUsed = cu; }
    this.punched = s.punched; this.linkStageFixed = s.linkStageFixed; this.mpGiven = s.mpGiven;
    this.actionKills = s.actionKills; this.massReported = s.massReported;
    this.executeRewards = s.executeRewards;
  }
  private flags(crit: boolean, finisher: boolean, blocked = false, juggle = false, downChase = false, launch = false): number {
    return this.extraFlags | (crit ? HitFlag.Crit : 0) | (launch ? HitFlag.Launch : 0) | (juggle ? HitFlag.Juggle : 0) | (downChase ? HitFlag.DownChase : 0) | (finisher ? HitFlag.Finisher : 0)
      | (this.source === HitSource.Skill ? HitFlag.Skill : 0) | (this.source === HitSource.Awaken ? HitFlag.Awaken : 0)
      | (this.multiHit ? HitFlag.MultiHit : 0) | (blocked ? HitFlag.Blocked : 0);
  }
  /** 이번 동작에서 처음 맞힌 순간 단발 카메라 펀치(타격 방향). 마무리타·스킬은 강하게, 일반 타격은 약하게 */
  private punch(finisher: boolean, dir: number): void {
    if (this.punched) return;
    this.punched = true;
    const strong = finisher || this.source === HitSource.Skill;
    const e = this.g.emit(Ev.CameraPunch);
    e.a = strong ? HIT_PUNCH.px : LIGHT_PUNCH.px; e.b = strong ? HIT_PUNCH.ms : LIGHT_PUNCH.ms; e.c = Math.sin(dir); e.d = Math.cos(dir);
  }
  /**
   * 방패 고블린 정면 막기: 기본공격(마무리타 제외)이 몹이 바라보는 쪽 ±60° 안에서 오면 막는다.
   * 경직·공중·등장 중에는 방패를 들지 못한다. 마무리타·스킬·각성은 방패를 뚫는다.
   */
  private guarded(i: number, finisher: boolean): boolean {
    const g = this.g, m = g.mobs, h = g.hero, st = m.state[i];
    if (m.kind[i] !== MobKind.ShieldGoblin || m.guardBroken[i] > 0 || finisher || this.source !== HitSource.Basic) return false;
    if (st === MobState.Stunned || st === MobState.Airborne || st === MobState.Spawning || st === MobState.Down || st === MobState.Wakeup) return false;
    const dx = h.x - m.x[i], dz = h.z - m.z[i], d = Math.hypot(dx, dz);
    if (d < 1e-6) return false;
    return (dx * Math.sin(m.facing[i]) + dz * Math.cos(m.facing[i])) / d >= GUARD_COS;
  }
  private contains(shape: number, x: number, z: number, a: number, b: number, width: number, tx: number, tz: number, radius: number): boolean {
    if (shape === TeleShape.Circle) return circleHit(x, z, tx, tz, a, radius);
    if (shape === TeleShape.Cone) return coneHit(x, z, b, a, width, tx, tz, radius);
    return segmentHit(x, z, a, b, width, tx, tz, radius);
  }
  /** mark이 null이면 현재 동작의 표식(touched·bossPulse)을 쓰고, 아니면 주어진 표식으로 같은 대상을 한 번만 맞힌다 */
  private resolve(shape: number, x: number, z: number, a: number, b: number, width: number, mult: number, finisher: boolean, stop: boolean, mark: HitMark | null): void {
    const g = this.g, m = g.mobs;
    for (let i = 0; i < m.capacity; i++) {
      // 기상 무적(COMBAT-FEEL 2.7): 맞지도, 표식에 찍히지도 않는다
      if (m.state[i] === MobState.Inactive || m.state[i] === MobState.Dying || m.state[i] === MobState.Wakeup) continue;
      if (mark ? mark.mobs[i] === 1 : this.touched[i] === this.pulse) continue;
      if (!this.contains(shape, x, z, a, b, width, m.x[i], m.z[i], g.mobController.radius(i))) continue;
      if (mark) mark.mobs[i] = 1; else this.touched[i] = this.pulse;
      this.damageMob(i, mult, finisher, stop);
    }
    const boss = g.boss;
    const bossFree = mark ? !mark.boss : this.bossPulse !== this.pulse;
    if (g.bossController.vulnerable && bossFree && this.contains(shape, x, z, a, b, width, boss.x, boss.z, boss.radius)) {
      if (mark) mark.boss = true; else this.bossPulse = this.pulse;
      this.damageBoss(mult, finisher, stop);
    }
  }
  /** 보스 한 번 타격(resolve·화살 첫 적중 공용). 표식은 호출자가 찍는다 */
  private damageBoss(mult: number, finisher: boolean, stop: boolean): void {
    const g = this.g, boss = g.boss;
    {
      const counter = this.takeJustDodgeCounter(), crit = counter || g.rng.next() < g.stats.critChance, damage = this.rollDamage(mult, crit, true);
      if (crit) this.critRefund();
      // 실제로 줄어든 체력만 기록·누적한다(초과 피해 제외).
      const dealt = Math.min(boss.hp, damage);
      // 레이드 넷 판: 로컬 HP는 서버 스냅이 올 때까지만 예측으로 깎고(1 아래로 내리지 않는다 — 처치·쓰러짐은 서버 boss 사건), 타격을 서버로 보낸다
      const net = g.raidNet.active;
      boss.hp = Math.max(net ? 1 : 0, boss.hp - damage); boss.hitTick = 0;
      if (net) g.raidNet.hit(damage);
      this.hit(boss.x, 1.5, boss.z, damage, 1, 0, headingTo(g.hero.x, g.hero.z, boss.x, boss.z, g.hero.facing), this.flags(crit, finisher));
      this.onHit(dealt, finisher);
      // 레이드: 영웅 위협(준 피해 그대로, RAID-PRD 3절)
      if (g.raid.active) g.raid.addThreat(0, dealt);
      if (stop) this.stop(crit || finisher ? HITSTOP.heavy : HITSTOP.normal);
      // 경직(그로기) 피해: 세트 효과 배율(장비 없으면 1)
      g.bossController.damaged(dealt * (counter ? JUST_DODGE.postureMult : 1) * g.stats.staggerMult);
      // 결투장식 KO: 보스를 쓰러뜨린 타격
      if (boss.hp === 0 && dealt > 0) {
        g.startKo("boss", boss.x, boss.z);
        // 피의 갈망: 스킬(판정 효과 포함)로 보스를 쓰러뜨려도 회복한다
        if (this.source === HitSource.Skill) this.lifesteal();
      }
    }
  }
  /**
   * 레이드 용병이 보스를 친다(RAID-PRD 3절). 영웅 콤보·각성·피해 합계·흡혈에는 넣지 않고 경직·페이즈·쓰러짐만 같은 길을 탄다.
   * slot = 파티 칸(1..3). 실제로 줄어든 HP를 돌려준다(맞을 수 없으면 0)
   */
  allyHitBoss(damage: number, x: number, z: number, slot: number): number {
    const g = this.g, boss = g.boss;
    if (!g.bossController.vulnerable || boss.hp <= 0) return 0;
    damage = Math.min(DAMAGE_CAP, Math.max(1, Math.round(damage)));
    const dealt = Math.min(boss.hp, damage);
    boss.hp = Math.max(0, boss.hp - damage); boss.hitTick = 0;
    this.hit(boss.x, 1.5, boss.z, damage, 1, slot, headingTo(x, z, boss.x, boss.z, 0), HitFlag.Ally);
    g.bossController.damaged(dealt);
    if (boss.hp === 0 && dealt > 0) g.startKo("boss", boss.x, boss.z);
    return dealt;
  }
  /**
   * 피해 굴림. 장비 옵션·세트: 스킬 피해(스킬 원인)·기본 공격 피해(기본 원인)·보스 피해(boss)·치명 피해(critMult = 1.8 + 옵션).
   * 장비가 없으면 모두 1·HERO.critMult라 기존 값과 같다. 능력치는 던전 입장 때 고정된 g.stats.
   */
  private rollDamage(mult: number, crit: boolean, boss = false, elite = false): number {
    const st = this.g.stats, h = this.g.hero;
    // 전설 고유 효과: 엘리트·보스 피해, HP 30% 이하 피해
    const low = st.lowHpDmg > 0 && h.hp <= h.maxHp * 0.3;
    const uniq = (boss || elite ? 1 + st.eliteDmg : 1) * (low ? 1 + st.lowHpDmg : 1);
    // 연출 훅(c017): 피해에 실제로 더해진 순간만, 1초에 한 번(b = 가산 ×1000)
    if (low && this.g.tick - this.lowHpCueTick >= LOW_HP_CUE_TICKS) { this.lowHpCueTick = this.g.tick; this.unique(UX.lowHpDmg, h.x, h.z, Math.round(st.lowHpDmg * 1000)); }
    const gear = (this.source === HitSource.Skill ? st.skillDmgMult : this.source === HitSource.Basic ? st.basicDmgMult : 1) * (boss ? st.bossDmgMult : 1) * uniq;
    return Math.min(DAMAGE_CAP, Math.max(1, Math.round(st.attack * mult * gear * this.linkMult() * this.g.rng.range(1 - HERO.damageJitter, 1 + HERO.damageJitter) * (crit ? st.critMult : 1))));
  }
  /**
   * 스킬 피해에만 붙는 연계 배율(LINK.damage[단계-1]). 2단 이상이면 연계 증폭 트리 가산(linkAdd)을 더한다.
   * 기본 공격·각성·판정 효과(plainHit)는 1.
   */
  linkMult(): number {
    const s = this.linkStageFixed >= 0 ? this.linkStageFixed : this.g.link.stage;
    if (this.plainHit || this.source !== HitSource.Skill || s < 1) return 1;
    const base = LINK.damage[Math.min(s, LINK.damage.length) - 1];
    return s >= 2 ? base + this.g.gmods.linkAdd : base;
  }
  private hit(x: number, y: number, z: number, damage: number, target: number, index: number, dir: number, flags: number): GameEvent {
    const e = this.g.emit(Ev.Hit);
    e.x = x; e.y = y; e.z = z; e.a = damage; e.b = target; e.c = index; e.d = dir; e.flags = flags;
    return e;
  }
  private onHit(damage: number, finisher = false): void {
    const g = this.g, c = g.combo;
    // 평타 MP(BALANCE-1028 1절): 기본 공격 동작의 첫 적중에 swing(마무리타 finisher) × 유니크 배율. 판정 효과(plainHit)는 채우지 않는다
    if (this.source === HitSource.Basic && !this.plainHit && !this.mpGiven) {
      this.mpGiven = true;
      const hh = g.hero;
      if (hh.mp < hh.maxMp) hh.mp = Math.min(hh.maxMp, hh.mp + (finisher ? MP_ECON.finisher : MP_ECON.swing) * g.stats.basicMpMult);
    }
    c.count++; c.timer = COMBO_COUNTER.reset; c.best = Math.max(c.best, c.count);
    if (this.source === HitSource.Basic) g.heroController.refreshLink();
    g.hero.awakenGauge = Math.min(1, g.hero.awakenGauge + AWAKEN.gainPerHit * g.awakenMult());
    g.result.damageDealt += damage;
    // 장비 흡혈: 준 피해 × 비율. 소수 몫을 모아 정수만큼 조용히 회복한다(타격마다 회복 이벤트·숫자를 띄우지 않는다)
    const ls = g.stats.lifesteal, h = g.hero;
    if (ls > 0 && damage > 0 && h.hp > 0 && h.hp < h.maxHp) {
      this.lifeBank += damage * ls;
      const n = Math.floor(this.lifeBank);
      if (n >= 1) { this.lifeBank -= n; h.hp = Math.min(h.maxHp, h.hp + n); }
    }
    for (let i = c.praiseTier; i < PRAISE_TIERS.length; i++) {
      if (c.count < PRAISE_TIERS[i].min) break;
      c.praiseTier = i + 1;
      const e = g.emit(Ev.ComboPraise); e.a = i + 1; e.b = c.count;
    }
  }

  /** 저스트 회피 뒤 동작의 첫 실제 타격만 강화한다. */
  private takeJustDodgeCounter(): boolean {
    if (!this.counterEligible || this.counterUsed || !this.g.hasJustDodgeCounter()) return false;
    this.counterUsed = true;
    return this.g.consumeJustDodgeCounter();
  }

  /** 처형 가능: 무방비 일반 몹(엘리트·보스 제외)을 기본 공격·스킬로 맞혔을 때 */
  private executable(i: number): boolean {
    const m = this.g.mobs, st = m.state[i];
    // 파티 판 꼭두각시는 처형하지 않는다(체력·처치는 서버 권위)
    return m.guardBroken[i] > 0 && m.elite[i] === 0 && m.hp[i] > 0 && this.source !== HitSource.Awaken && !this.g.party.owns(i)
      && st !== MobState.Spawning && st !== MobState.Dying && st !== MobState.Inactive;
  }

  /**
   * 처형(COMBAT-FEEL 2.4): 남은 체력을 전부 깎고 보상(회복·각성·히트스톱·카메라 펀치)을 준다.
   * 보상은 한 동작당 EXECUTE.maxRewardsPerAction회까지, 초과분은 처치만.
   */
  private execute(i: number): void {
    const g = this.g, m = g.mobs, h = g.hero;
    const dir = headingTo(h.x, h.z, m.x[i], m.z[i], h.facing), dealt = m.hp[i];
    m.hp[i] = 0; m.hitTick[i] = 0;
    this.hit(m.x[i], m.y[i] + 1, m.z[i], dealt, 0, i, dir, HitFlag.Finisher | HitFlag.Execute | (this.source === HitSource.Skill ? HitFlag.Skill : 0));
    this.onHit(dealt);
    const rewarded = this.executeRewards < EXECUTE.maxRewardsPerAction;
    let healed = 0;
    if (rewarded) {
      this.executeRewards++;
      healed = Math.min(h.maxHp - h.hp, Math.max(1, Math.round(h.maxHp * EXECUTE.healRatio)));
      if (healed > 0) {
        h.hp += healed;
        const heal = g.emit(Ev.HeroHeal); heal.x = h.x; heal.z = h.z; heal.a = healed; heal.b = 1;
      }
      h.awakenGauge = Math.min(1, h.awakenGauge + EXECUTE.awakenGain * g.stats.awakenGainMult);
      this.stop(EXECUTE.hitstop);
      const punch = g.emit(Ev.CameraPunch);
      punch.a = EXECUTE.punchPx; punch.b = EXECUTE.punchMs; punch.c = Math.sin(dir); punch.d = Math.cos(dir);
    }
    const e = g.emit(Ev.Execute);
    e.x = m.x[i]; e.y = m.y[i]; e.z = m.z[i]; e.a = m.kind[i]; e.b = i; e.c = healed; e.d = dir; e.flags = rewarded ? 1 : 0;
    this.killMob(i, this.source, true, HitFlag.Execute);
    // 피의 갈망(docs/SKILL-TREE.md): 스킬 피해 처치는 처형이어도 회복한다(처형 보상 상한과 별개)
    if (this.source === HitSource.Skill) this.lifesteal();
    this.actionKills++;
    if (!this.massReported && this.actionKills >= MOBS.massKillThreshold) {
      this.massReported = true;
      g.emit(Ev.MassKill).a = this.actionKills;
      g.startMassSlow();
    }
  }

  private damageMob(i: number, mult: number, finisher: boolean, stop: boolean): void {
    const g = this.g, m = g.mobs, h = g.hero;
    // 처형도 첫 실제 타격이다: 저스트 회피 반격을 여기서 소비해 다음 대상·다음 동작으로 넘어가지 않게 한다
    if (this.executable(i)) { this.takeJustDodgeCounter(); this.execute(i); return; }
    const blocked = this.guarded(i, finisher);
    // 공중 연타: 뜬 몹에 들어가는 타격은 피해 +15%(COMBAT-FEEL 2.2)
    const air = m.state[i] === MobState.Airborne;
    // 다운 추격(COMBAT-FEEL 2.7): 쓰러진 몹은 피해 60%
    const down = m.state[i] === MobState.Down;
    const counter = this.takeJustDodgeCounter();
    const crit = !blocked && (counter || g.rng.next() < g.stats.critChance);
    if (crit) this.critRefund();
    const damage = Math.min(DAMAGE_CAP, Math.round(this.rollDamage(mult, crit, false, m.elite[i] !== 0) * (blocked ? SHIELD_GUARD.damageMult : 1) * (air ? JUGGLE.damageMult : 1) * (down ? HIT_STATE.downDamageMult : 1)));
    const dealt = Math.min(m.hp[i], damage);
    const dir = headingTo(h.x, h.z, m.x[i], m.z[i], h.facing);
    m.hp[i] = Math.max(0, m.hp[i] - damage); m.hitTick[i] = 0;
    // 파티 판(레인 D): 피해를 서버로 보내고, 로컬 체력은 서버 die 전까지 1 아래로 내리지 않는다(숫자·히트스톱·넉백 손맛은 그대로)
    if (g.party.hit(i, damage) && m.hp[i] < 1) m.hp[i] = 1;
    // 런처가 띄우기를 시도하는 타격(엘리트 저항으로 못 띄울 수는 있다). 이미 뜬 몹은 재띄움, 다운·기상 몹은 아니다
    const launching = this.launch !== null && !blocked && g.mobController.launchable(i);
    const hitEv = this.hit(m.x[i], m.y[i] + 1, m.z[i], damage, 0, i, dir, this.flags(crit, finisher, blocked, air, down, launching));
    this.onHit(dealt, finisher);
    // 방패·엘리트는 막힘/피해 비례, 일반 몹은 타격 세기별 작은 막대(POSTURE_NORMAL)
    const posture = m.kind[i] !== MobKind.ShieldGoblin && m.elite[i] === 0
      ? (this.source === HitSource.Awaken ? POSTURE_NORMAL.awaken : this.source === HitSource.Skill ? POSTURE_NORMAL.skill : finisher ? POSTURE_NORMAL.finisher : POSTURE_NORMAL.basic)
      : (blocked ? POSTURE.shieldBlocked : 0)
        + (m.elite[i] ? (dealt / Math.max(1, m.maxHp[i])) * POSTURE.eliteDamageRatio : 0)
        + (finisher ? POSTURE.finisherBonus : 0)
        + (this.source === HitSource.Skill ? POSTURE.skillBonus : 0);
    const broken = m.hp[i] > 0 && g.mobController.addPosture(i, posture * (counter ? JUST_DODGE.postureMult : 1), Math.sin(dir), Math.cos(dir));
    if (stop) this.stop(crit || finisher ? HITSTOP.heavy : HITSTOP.normal);
    this.punch(finisher, dir);
    if (m.hp[i] === 0) {
      this.killMob(i, this.source, finisher || this.source === HitSource.Skill);
      // 피의 갈망: 스킬 피해로 쓰러뜨리면 최대 HP의 일정 비율을 회복한다(판정 효과 포함)
      if (this.source === HitSource.Skill) this.lifesteal();
      this.actionKills++;
      if (!this.massReported && this.actionKills >= MOBS.massKillThreshold) {
        this.massReported = true;
        g.emit(Ev.MassKill).a = this.actionKills;
        g.startMassSlow();
      }
    } else if (!blocked && (!broken || this.launch)) {
      const mc = g.mobController;
      if (this.launch) {
        // 런처(COMBAT-FEEL 2.11): 일반 몹은 즉시 띄움, 엘리트는 저항 굴림, 멧돼지는 면역(피해만).
        // 이 타격으로 자세가 무너져도(무방비 경직) 띄움이 우선한다 — 무방비 상태로 떠서 공중 추가타를 받고, 착지는 무방비 규칙(경직)을 따른다
        // 타격 전 launchable()로는 슈퍼아머(멧돼지)가 자세가 무너져 뜨는 경우를 놓치므로, 실제로 띄웠다면 Hit 이벤트에 Launch를 보탠다
        if (mc.launch(i, Math.sin(dir), Math.cos(dir), this.launch)) hitEv.flags |= HitFlag.Launch;
      } else if (!mc.armored(i) && !(this.source === HitSource.Basic && !finisher && mc.committed(i))) {
        // 슈퍼아머(엘리트·멧돼지)와 등장 연출 중인 몹은 피해만 받고 경직·넉백 없이 동작을 이어간다.
        // 막힌 타격은 방패에 튕겨 경직이 없다. 공격을 결심한 몹(선딜 후반)은 가벼운 타격에 끊기지 않는다.
        // 다운·기상 중인 몹은 knockback이 상태를 바꾸지 않는다.
        mc.knockback(i, Math.sin(dir), Math.cos(dir), finisher);
      } else if (finisher && mc.eliteLaunchable(i)) {
        // 엘리트는 슈퍼아머라 경직은 없지만 띄우는 타격은 확률(HIT_STATE.eliteResist)로 저항한다
        mc.knockback(i, Math.sin(dir), Math.cos(dir), true);
      }
    }
  }
  /** 피의 갈망(스킬 처치 회복): 최대 HP × lifesteal, 최소 1. 체력이 가득 찼으면 이벤트를 내지 않는다 */
  private lifesteal(): void {
    const g = this.g, h = g.hero, ratio = g.gmods.lifesteal;
    if (ratio <= 0 || h.hp <= 0 || h.hp >= h.maxHp) return;
    const before = h.hp;
    h.hp = Math.min(h.maxHp, h.hp + Math.max(1, Math.round(h.maxHp * ratio)));
    const e = g.emit(Ev.HeroHeal); e.x = h.x; e.z = h.z; e.a = h.hp - before; e.b = 1;
  }
  /** 몹 처치 기록(정상 타격·QA 공용): 상태, 처치 수, 목표, Kill 이벤트. heavy면 더 높고 멀리 튕겨 날아간다 */
  killMob(i: number, cause: HitSourceId, heavy = false, eventFlags = 0): void {
    const g = this.g, m = g.mobs, h = g.hero;
    if (m.state[i] === MobState.Inactive || m.state[i] === MobState.Dying) return;
    const dir = headingTo(h.x, h.z, m.x[i], m.z[i], h.facing);
    const power = cause === HitSource.Awaken ? KillPower.Awaken : heavy ? KillPower.Heavy : KillPower.Basic;
    // 처치 EXP: 지역 배율 × 스테이지 배율(스테이지 판, D3a) × 난이도(하드 ×2.3, D3b). 자유 입장은 스테이지 1
    const plan = g.flowController.plan;
    // 파티 판: 처치 EXP·드롭·스페셜 보상은 없다(보상은 서버 우편, PARTY-TRADE-PRD 3.6)
    const party = g.party.active;
    const xp = party ? 0 : Math.round(killXp(m.kind[i], m.elite[i] !== 0, g.regionKey, Math.max(1, plan.index)) * plan.xpDiff);
    g.mobController.kill(i, Math.sin(dir), Math.cos(dir), power);
    g.result.kills++;
    g.flowController.mobKilled(i);
    const e = g.emit(Ev.Kill);
    e.x = m.x[i]; e.z = m.z[i]; e.a = m.kind[i]; e.b = i; e.c = dir; e.d = cause; e.flags = eventFlags;
    if (xp > 0) g.grantXp(xp, XpCause.Kill, m.x[i], m.z[i]);
    // 전설 고유 효과(처치): 회복·MP·각성
    const st = g.stats;
    if (st.killHeal > 0 && h.hp > 0 && h.hp < h.maxHp) { const n = Math.min(h.maxHp - h.hp, Math.max(1, Math.round(h.maxHp * st.killHeal))); h.hp += n; this.unique(UX.killHeal, m.x[i], m.z[i], n); }
    if (st.killMp > 0 && h.mp < h.maxMp) { const n = Math.min(h.maxMp - h.mp, st.killMp); h.mp += n; this.unique(UX.killMp, m.x[i], m.z[i], n); }
    if (st.killAwaken > 0) { h.awakenGauge = Math.min(1, h.awakenGauge + st.killAwaken); this.unique(UX.killAwaken, m.x[i], m.z[i], Math.round(st.killAwaken * 1000)); }
    // 바닥 드롭(D2a): 일반 몹 확률·엘리트 확정. 지역보스 스테이지 일반 웨이브는 전설을 굴리지 않는다
    // 스페셜 몬스터(황금 고블린·챔피언)는 일반 드롭에 스페셜 보상을 더해 한 곳에서 처리한다(game/special.ts)
    if (party) { /* 보상 없음 */ } else if (m.special[i] !== 0) g.special.onKilled(i); else g.loot.onMobKilled(m.x[i], m.z[i], m.elite[i] !== 0);
    // 연속 처치(폭주): 환경 처치는 세지 않는다
    if ((eventFlags & HitFlag.Environment) === 0) g.rampageController.onKill(m.x[i], m.z[i]);
    // 결투장식 KO: 엘리트이거나 방의 마지막 몹이면 세계를 잠깐 늦춘다(GameSim.startKo)
    if (m.elite[i]) g.startKo("elite", m.x[i], m.z[i]);
    else if (g.flowController.finalKill()) g.startKo("room", m.x[i], m.z[i]);
  }
  /** 환경 피해는 영웅 공격의 콤보·치명·가드와 무관하며 처치는 공통 경로로 기록한다. */
  hazardMob(i: number, damage: number): void {
    const m = this.g.mobs;
    if (m.state[i] === MobState.Inactive || m.state[i] === MobState.Dying || m.state[i] === MobState.Wakeup) return;
    m.hp[i] = Math.max(0, m.hp[i] - damage); m.hitTick[i] = 0;
    if (this.g.party.hit(i, damage) && m.hp[i] < 1) m.hp[i] = 1;
    this.hit(m.x[i], m.y[i] + 1, m.z[i], damage, 0, i, 0, HitFlag.Environment);
    if (m.hp[i] === 0) this.killMob(i, HitSource.Basic, false, HitFlag.Environment);
  }
  /**
   * 영웅이 받는 피해(배율 적용 뒤)에 장비 방어 효과를 건다: 전설 고유 효과 보호막이 먼저 흡수하고, 남은 피해가 쓰러뜨리면 세트 버팀으로 HP 1을 남긴다.
   * 몹 타격(hurtHero)과 환경 피해(burnHero 용암 화상)가 같은 규칙을 쓴다. 남은 피해를 돌려준다
   */
  private absorb(damage: number): number {
    const h = this.g.hero;
    // 전설 고유 효과 보호막이 먼저 흡수한다
    if (this.shield > 0) { const ab = Math.min(this.shield, damage); this.shield -= ab; damage -= ab; if (this.shield === 0) this.shieldTicks = 0; }
    // 세트 효과 버팀: 쓰러질 피해를 판마다 정해진 횟수만큼 HP 1로 버틴다
    // HP가 정확히 1이어도(양수면) 버팀을 쓰고 HP 1을 유지한다(피해 0, PR #154 Codex 2차 P2)
    if (damage >= h.hp && this.lastStandLeft > 0 && h.hp > 0) { this.lastStandLeft--; damage = h.hp - 1; }
    return damage;
  }
  /** 환경·상태 피해. status 0 = 용암 화상(Ev.LavaBurn), 1 = 서리 동상, 2 = 중독(Ev.HeroStatus a 3) — D3a */
  burnHero(damage: number, status = 0): void {
    const g = this.g, h = g.hero;
    if (h.invulnerable || h.hp <= 0 || !combatScene(g.flow.scene) || g.flow.clearHold > 0) return;
    // 불굴(트리)·장비 방어/피해 감소: 용암 화상도 받는 피해 배율을 받는다(hurtHero와 같은 규칙, 정수, 최소 1)
    damage = Math.max(1, Math.round(damage * g.gmods.damageTakenMult * g.gearDamageTakenMult()));
    // 보호막 흡수·세트 버팀도 hurtHero와 같은 규칙(c015: 화상이 보호막을 건너뛰고 버팀 없이 쓰러뜨렸다, PR #154 리뷰 이월)
    damage = this.absorb(damage);
    // 보호막이 다 흡수했으면 데미지 숫자 '0'·화상 효과음을 내지 않는다(PR #157 리뷰 P2)
    if (damage <= 0) return;
    h.hp = Math.max(0, h.hp - damage);
    this.hit(h.x, h.y + 1, h.z, damage, 2, 0, 0, HitFlag.Environment);
    if (status === 0) { const e = g.emit(Ev.LavaBurn); e.x = h.x; e.z = h.z; e.a = damage; }
    else { const e = g.emit(Ev.HeroStatus); e.x = h.x; e.z = h.z; e.a = 3; e.b = damage; e.c = status; }
    if (h.hp === 0) g.flowController.defeat();
  }
  /** 영웅 피격. 데미지 숫자는 Hit(b=2)로, HUD 반응은 HeroHurt로 낸다. 무적이면 false */
  hurtHero(damage: number, fromX: number, fromZ: number, heavy = false, source = 0, eventFlags = 0): boolean {
    const g = this.g, h = g.hero;
    // 클리어 연출 대기(마지막 적이 쓰러지는 동안): 남은 경고·장판이 쓰러뜨려 패배로 가지 않게 한다
    if (g.flow.clearHold > 0) return false;
    // 2026-10-20 수치 재설계: 작은 수에 지역 배율(1.2)을 곱하면 소수가 생긴다. 체력·표시·저장은 정수로 둔다
    // 불굴(트리)·장비 방어(몹 레벨 곡선)·받는 피해 감소 옵션·세트: 받는 피해 배율. 정수로 맞춘 뒤 최소 1
    damage = Math.max(1, Math.round(damage * g.gmods.damageTakenMult * g.gearDamageTakenMult()));
    if (h.invulnerable) {
      if (h.act === HeroAct.Dodge && h.actTick < JUST_DODGE.window && g.awaken.phase === 0 && combatScene(g.flow.scene) && h.hp > 0) {
        g.triggerJustDodge(source);
      }
      return false;
    }
    if (h.act === HeroAct.Dead || !combatScene(g.flow.scene)) return false;
    damage = this.absorb(damage);
    h.hp = Math.max(0, h.hp - damage);
    h.awakenGauge = Math.min(1, h.awakenGauge + AWAKEN.gainPerHurt * g.awakenMult());
    const dir = headingTo(fromX, fromZ, h.x, h.z, h.facing + Math.PI);
    this.hit(h.x, h.y + 1, h.z, damage, 2, 0, dir, eventFlags);
    const e = g.emit(Ev.HeroHurt); e.x = h.x; e.z = h.z; e.a = damage;
    if (heavy) {
      this.stop(HITSTOP.boss);
      const punch = g.emit(Ev.CameraPunch);
      punch.a = CAMERA_PUNCH.bossPx; punch.b = CAMERA_PUNCH.bossMs; punch.c = Math.sin(dir); punch.d = Math.cos(dir);
    }
    if (h.hp === 0) g.flowController.defeat();
    else g.heroController.hurt(heavy);
    return true;
  }
}
